// Who the reader is, for the whole SPA.
//
// The five pages were server components that called `getSession()` inline and
// `redirect("/")` when it was null. A client-rendered app has a state those
// pages could not have — "we do not know yet" — and every gate here has to
// account for it: redirecting while the answer is outstanding signs a reader
// out on every page load.
//
// One fetch per mount of the provider, shared through context. Not one per
// page: `/api/session` costs a DID → users.id round-trip, and four routes each
// asking on navigation would be four of them for an answer that does not move.

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { fetchSession } from "../lib/api";

export type SessionState =
  /** The answer is outstanding. Render nothing that depends on identity. */
  | { status: "unknown" }
  | { status: "signed-out" }
  | { status: "signed-in"; did: string; userId: string | null };

const SessionContext = createContext<SessionState>({ status: "unknown" });

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({ status: "unknown" });

  useEffect(() => {
    let ignore = false;
    const controller = new AbortController();
    fetchSession(controller.signal).then(
      (session) => {
        if (ignore) return;
        setState(
          session.did === null
            ? { status: "signed-out" }
            : { status: "signed-in", did: session.did, userId: session.userId },
        );
      },
      () => {
        // A failed identity fetch is treated as signed out, deliberately. The
        // alternative is leaving the whole app in "unknown" forever, which
        // renders a blank page with no way out; signed out at least offers the
        // sign-in form, and signing in re-runs this.
        if (!ignore) setState({ status: "signed-out" });
      },
    );
    return () => {
      ignore = true;
      controller.abort();
    };
  }, []);

  return (
    <SessionContext.Provider value={state}>{children}</SessionContext.Provider>
  );
}

export function useSession(): SessionState {
  return useContext(SessionContext);
}
